import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ButtonEditor } from "./ButtonEditor";
import { Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ButtonData {
  text: string;
  url: string;
  variant: "primary" | "outline";
}

interface SectionData {
  id: string;
  section_type: string;
  title: string;
  subtitle: string;
  content: string;
  youtube_url: string;
  image_url: string;
  background_color: string;
  buttons: ButtonData[];
  is_visible: boolean;
  order_index: number;
}

interface SectionEditorProps {
  section: SectionData;
  onChange: (updated: SectionData) => void;
}

const sectionLabels: Record<string, string> = {
  banner: "🖼 Banner (Topo)",
  countdown: "⏱ Contador Regressivo",
  hero: "🎬 Hero (Topo)",
  about: "📋 Sobre a Oferta",
  benefits: "✅ Benefícios",
  cta: "🚀 CTA Final",
};

export function SectionEditor({ section, onChange }: SectionEditorProps) {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const update = (field: string, value: any) => {
    onChange({ ...section, [field]: value });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Formato não suportado", description: "Use JPG, PNG ou WebP.", variant: "destructive" });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Arquivo muito grande", description: "Máximo 2MB.", variant: "destructive" });
      return;
    }

    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('banners')
        .upload(fileName, file, { contentType: file.type, upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('banners')
        .getPublicUrl(fileName);

      update("image_url", publicUrl);
    } catch (error) {
      console.error('Erro no upload:', error);
      toast({ title: "Erro no upload", description: "Não foi possível enviar a imagem.", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            {sectionLabels[section.section_type] || section.section_type}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor={`visible-${section.id}`} className="text-sm text-muted-foreground">Visível</Label>
            <Switch
              id={`visible-${section.id}`}
              checked={section.is_visible}
              onCheckedChange={v => update("is_visible", v)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {section.section_type === "banner" ? (
          <>
            <div>
              <Label>Imagem do Banner (800×400px)</Label>
              {section.image_url ? (
                <div className="relative mt-2 rounded-lg overflow-hidden border border-border">
                  <img
                    src={section.image_url}
                    alt="Preview do banner"
                    className="w-full h-40 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => update("image_url", "")}
                    className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:opacity-80 transition-opacity"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="relative mt-2 flex flex-col items-center justify-center border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors">
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm font-medium text-muted-foreground">
                    {isUploading ? "Enviando..." : "Clique para selecionar uma imagem"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">JPG, PNG ou WebP • Máx 2MB</p>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={isUploading}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  />
                </div>
              )}
            </div>
            <div>
              <Label>Link de destino (ao clicar no banner)</Label>
              <Input
                placeholder="https://..."
                value={(Array.isArray(section.buttons) && section.buttons[0]?.url) || ""}
                onChange={e => {
                  const url = e.target.value;
                  update("buttons", [{ text: "banner-link", url, variant: "primary" as const }]);
                }}
              />
            </div>
          </>
        ) : section.section_type === "countdown" ? (
          <>
            <div>
              <Label>Texto acima do contador</Label>
              <Input value={section.title} onChange={e => update("title", e.target.value)} placeholder="A oferta expira em:" />
            </div>
            <div>
              <Label>Data/hora alvo</Label>
              <Input
                type="datetime-local"
                value={section.content ? section.content.slice(0, 16) : ""}
                onChange={e => update("content", e.target.value ? new Date(e.target.value).toISOString() : "")}
              />
            </div>
            <div>
              <Label>Cor de fundo (opcional)</Label>
              <Input
                placeholder="#f5f5f5 ou vazio para padrão"
                value={section.background_color}
                onChange={e => update("background_color", e.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Título</Label>
                <Input value={section.title} onChange={e => update("title", e.target.value)} />
              </div>
              <div>
                <Label>Subtítulo</Label>
                <Input value={section.subtitle} onChange={e => update("subtitle", e.target.value)} />
              </div>
            </div>

            <div>
              <Label>Conteúdo {section.section_type === "benefits" && "(separe itens com |)"}</Label>
              <Textarea
                value={section.content}
                onChange={e => update("content", e.target.value)}
                rows={4}
              />
            </div>

            {section.section_type === "hero" && (
              <div>
                <Label>URL do YouTube</Label>
                <Input
                  placeholder="https://youtube.com/watch?v=..."
                  value={section.youtube_url}
                  onChange={e => update("youtube_url", e.target.value)}
                />
              </div>
            )}

            {section.section_type === "about" && (
              <div>
                <Label>Imagem</Label>
                {section.image_url ? (
                  <div className="relative mt-2 rounded-lg overflow-hidden border border-border">
                    <img src={section.image_url} alt="Preview" className="w-full h-40 object-cover" />
                    <button
                      type="button"
                      onClick={() => update("image_url", "")}
                      className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:opacity-80 transition-opacity"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative mt-2 flex flex-col items-center justify-center border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors">
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <p className="text-sm font-medium text-muted-foreground">
                      {isUploading ? "Enviando..." : "Clique para selecionar uma imagem"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">JPG, PNG ou WebP • Máx 2MB</p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    />
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Cor de fundo (opcional)</Label>
              <Input
                placeholder="#f5f5f5 ou vazio para padrão"
                value={section.background_color}
                onChange={e => update("background_color", e.target.value)}
              />
            </div>

            <ButtonEditor
              buttons={Array.isArray(section.buttons) ? section.buttons : []}
              onChange={btns => update("buttons", btns)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
